import { FormControl, FormHelperText, IconButton } from "@material-ui/core";
import React, { useEffect, useState, memo } from "react";
import { useStyles } from "./styles";
import styled from "styled-components";
import { colors } from "theme/Theme";
import { DefaultText } from "theme/GlobalElements";
import { Controller } from "react-hook-form";
import { MdFileUpload, MdDelete } from "react-icons/md";

interface ImageFieldProps {
  control: any;
  reset: boolean | undefined;
  setValue: any;
  content: Content;
  required: boolean;
  ratio?: string;
  maxSizeBytes?: number;
}

export interface Content {
  id: number;
  name: string;
  file_name: string | undefined;
  contentType: string | undefined;
  image: string | undefined;
  is_main_image: boolean;
}

const ImageField: React.FC<ImageFieldProps> = ({
  content,
  control,
  reset,
  required,
  setValue,
  ratio,
  maxSizeBytes = 500000,
}) => {
  const { id, name, contentType, file_name, image, is_main_image } = content;
  const classes = useStyles();
  const [imageUri, setImageUri] = useState(
    contentType && image ? `data:${contentType};base64,${image}` : undefined
  );
  const [error, setError] = useState("");
  useEffect(() => {
    if (reset !== undefined) {
      setImageUri(undefined);
      setError("");
    }
  }, [reset]);

  useEffect(() => {
    setValue(name, content);
    setImageUri(
      contentType && image ? `data:${contentType};base64,${image}` : undefined
    );
  }, [file_name]);

  const handleImageChange = (e: any, onChange: any) => {
    e.preventDefault();
    if (checkMimeType(e) && checkFileSize(e)) {
      let reader = new FileReader();
      let _file = e.target.files[0];

      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setImageUri(reader.result);
        }
      };
      if (_file instanceof Blob) {
        reader.readAsDataURL(_file);
        const content = { id, is_main_image: id === 0, file: _file };

        setValue(name, content);
      }
    }
  };

  const checkMimeType = (event: any) => {
    let files = event.target.files;
    let err = "";
    const types = ["image/png", "image/jpeg"];
    for (var x = 0; x < files.length; x++) {
      if (types.every((type) => files[x].type !== type)) {
        err += files[x].type + " Формата не се поддържа\n";
      }
    }

    if (err !== "") {
      event.target.value = null;
      setError(err);
      return false;
    }
    setError("");
    return true;
  };

  const checkFileSize = (event: any) => {
    let files = event.target.files;
    let size = maxSizeBytes;
    let err = "";
    for (var x = 0; x < files.length; x++) {
      if (files[x].size > size) {
        console.log(files[x].size);
        err += `Максимален размер ${maxSizeBytes / 1000} KB за снимка.\n`;
      }
    }
    if (err !== "") {
      event.target.value = null;
      setError(err);
      return false;
    }
    setError("");
    return true;
  };

  const onDelete = () => {
    setImageUri(undefined);
    setValue(name, undefined);
    setError("");
  };

  let imagePreview = null;
  if (imageUri) {
    imagePreview = <img src={imageUri} alt={"photo"} />;
  } else {
    imagePreview = (
      <DefaultText color="#656565">
        {is_main_image ? "Основна снимка" : `Снимка ${id + 1}`}
        {required && <RequiredAsterisk aria-hidden="true"> *</RequiredAsterisk>}
      </DefaultText>
    );
  }

  return (
    <Form required={required} className={classes.formControl}>
      <Buttons>
        <IconButton
          aria-label="Добави"
          component="label"
          htmlFor={name}
        >
          <MdFileUpload size="1.5rem" />
          <Controller
            name={name}
            control={control}
            defaultValue={content}
            render={({ onChange, name }) => (
              <input
                type="file"
                hidden
                name={name}
                id={name}
                aria-label={
                  is_main_image
                    ? "Избери основна снимка"
                    : `Избери снимка ${id + 1}`
                }
                alt="снимка"
                onChange={(e) => handleImageChange(e, onChange)}
              />
            )}
          />
        </IconButton>
        <IconButton aria-label="Изтрий" onClick={onDelete}>
          <MdDelete size="1.5rem" />
        </IconButton>
      </Buttons>
      <ImageContainer ratio={ratio}>
        <ImagePreview className="imgPreview">{imagePreview}</ImagePreview>
      </ImageContainer>
      {error !== "" && <FormHelperText error={true}>{error}</FormHelperText>}

      {required && <FormHelperText>Задължително</FormHelperText>}
    </Form>
  );
};
const Form = styled(FormControl)`
  border: 1px solid #eaeaea;
  padding: 5px;
  box-sizing: content-box;
  border-radius: 5px;
`;

const UploadBtn = styled.div`
  position: relative;
  overflow: hidden;
  display: inline-block;
`;

const ImageContainer = styled.div<{ ratio?: string }>`
  margin: 10px 0;
  width: 100%;
  padding-top: ${({ ratio }) => (ratio ? ratio : "75%")}; /* 4:3 Aspect Ratio */
  position: relative;
  background-color: ${colors.gray};
  text-align: center;
  border: 1px #d0d0d0 solid;
`;
const ImagePreview = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Buttons = styled.div`
  display: inline-flex;
  justify-content: space-around;
`;

const RequiredAsterisk = styled.span`
  color: #d32f2f;
`;

export default memo(ImageField);
